import { ConfigurationWaterfall } from './configuration-waterfall.service';

export type Environment = 'stable' | 'alfa' | 'beta' | 'prod';
export type Section = 'overview' | 'compare' | 'cross';
export type RenderMode = 'rendered' | 'raw';
export type PayloadKind = 'Application' | 'ApiCore';
export type TableItem =
  | { kind: 'group'; key: string; count: number }
  | { kind: 'row'; row: ConfigurationWaterfall };
export type CrossItem =
  | { kind: 'group'; key: string; count: number }
  | { kind: 'row'; key: string; type: string; organizationName: string; left: ConfigurationWaterfall | null; right: ConfigurationWaterfall | null };
export type Notice = { kind: 'key'; key: string } | { kind: 'text'; text: string };
export interface DiffLine { text: string; changed: boolean }
export type TextFn = (key: string) => string;

export const environments: { id: Environment; label: string }[] = [
  { id: 'stable', label: 'Stable' }, { id: 'alfa', label: 'Alpha' },
  { id: 'beta', label: 'Beta' }, { id: 'prod', label: 'Prod' },
];
const environmentPairs = [['stable', 'alfa'], ['alfa', 'beta'], ['beta', 'prod']] as const;
const editorColors: Record<Environment, string> = {
  stable: '#6d4aff',
  alfa: '#2f9e44',
  beta: '#12a37a',
  prod: '#e03131',
};
export const authorColors = ['#6d4aff', '#2f9e44', '#12a37a', '#e03131', '#e08a00', '#1c7ed6'];
const pairCountCache = new WeakMap<ConfigurationWaterfall, Map<string, number>>();
const crossCountCache = new WeakMap<ConfigurationWaterfall, Map<string, number>>();

export function environmentLabel(environment: Environment): string {
  return environments.find(item => item.id === environment)?.label ?? environment;
}

export function identity(row: ConfigurationWaterfall): string {
  return JSON.stringify([row.type, row.key, row.organizationName, row.swaggerName, row.isTemplate]);
}

export function familyId(row: ConfigurationWaterfall): string {
  return JSON.stringify([row.type, row.key, row.organizationName, row.isTemplate]);
}

export function filterRows(rows: ConfigurationWaterfall[], query: string): ConfigurationWaterfall[] {
  const needle = query.trim().toLocaleLowerCase();
  return rows.filter(row => !needle || [
    row.key, row.type, row.organizationName, row.swaggerName,
    row.stableUpdatedBy, row.alfaUpdatedBy, row.betaUpdatedBy, row.prodUpdatedBy,
  ].some(value => value?.toLocaleLowerCase().includes(needle)));
}

export function groupRows(rows: ConfigurationWaterfall[]): TableItem[] {
  const groups = new Map<string, ConfigurationWaterfall[]>();
  for (const row of rows) {
    const items = groups.get(row.key);
    if (items) items.push(row);
    else groups.set(row.key, [row]);
  }
  const table: TableItem[] = [];
  for (const [key, grouped] of [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0], undefined, { sensitivity: 'base' }))) {
    table.push({ kind: 'group', key, count: grouped.length });
    for (const row of grouped) table.push({ kind: 'row', row });
  }
  return table;
}

export function specNames(rows: ConfigurationWaterfall[]): string[] {
  const families = new Map<string, Set<string>>();
  for (const row of rows) {
    const names = families.get(familyId(row)) ?? new Set<string>();
    names.add(row.swaggerName);
    families.set(familyId(row), names);
  }
  const names = new Set<string>();
  for (const group of families.values()) {
    if (group.size < 2) continue;
    for (const name of group) names.add(name);
  }
  return [...names].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

export function payload(row: ConfigurationWaterfall, environment: Environment, kind: PayloadKind): string | null {
  if (kind === 'Application') {
    switch (environment) {
      case 'stable': return row.stableApplicationData;
      case 'alfa': return row.alfaApplicationData;
      case 'beta': return row.betaApplicationData;
      case 'prod': return row.prodApplicationData;
    }
  }
  switch (environment) {
    case 'stable': return row.stableApiCoreData;
    case 'alfa': return row.alfaApiCoreData;
    case 'beta': return row.betaApiCoreData;
    case 'prod': return row.prodApiCoreData;
  }
}

export function hasApplication(row: ConfigurationWaterfall, environment: Environment): boolean {
  return payload(row, environment, 'Application') !== null;
}

export function hasApiCore(row: ConfigurationWaterfall, environment: Environment): boolean {
  return payload(row, environment, 'ApiCore') !== null;
}

export function hasData(row: ConfigurationWaterfall, environment: Environment): boolean {
  return hasApplication(row, environment) || hasApiCore(row, environment);
}

export function contentLabel(row: ConfigurationWaterfall, environment: Environment, text: TextFn): string {
  const parts: string[] = [];
  if (hasApplication(row, environment)) parts.push(text('fields.application'));
  if (hasApiCore(row, environment)) parts.push(text('fields.apiCore'));
  return parts.join(' · ') || '—';
}

export function displayText(value: string | null, json: boolean, mode: RenderMode, text: TextFn): string {
  if (value === null) return text('fields.null');
  if (value === '') return text('fields.empty');
  let shown = value;
  if (json) {
    try { shown = JSON.stringify(JSON.parse(value), null, 2); }
    catch { shown = value; }
  }
  return mode === 'rendered' ? shown.replaceAll('\\n', '\n') : shown;
}

export function prettyJson(value: string | null): string | null {
  if (value === null) return null;
  try { return JSON.stringify(JSON.parse(value), null, 2); }
  catch { return value; }
}

export function diffLines(left: string, right: string): { left: DiffLine[]; right: DiffLine[] } {
  const a = left.split('\n');
  const b = right.split('\n');
  const next = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0));
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) next[i][j] = a[i] === b[j] ? next[i + 1][j + 1] + 1 : Math.max(next[i + 1][j], next[i][j + 1]);
  }
  const leftLines: DiffLine[] = [];
  const rightLines: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      leftLines.push({ text: a[i], changed: false });
      rightLines.push({ text: b[j], changed: false });
      i++;
      j++;
    } else if (next[i + 1][j] >= next[i][j + 1]) leftLines.push({ text: a[i++], changed: true });
    else rightLines.push({ text: b[j++], changed: true });
  }
  while (i < a.length) leftLines.push({ text: a[i++], changed: true });
  while (j < b.length) rightLines.push({ text: b[j++], changed: true });
  return { left: leftLines, right: rightLines };
}

export function changedPayloadLines(leftValue: string | null, rightValue: string | null, json: boolean, mode: RenderMode, text: TextFn): number {
  if (leftValue === null || rightValue === null) return 0;
  const diff = diffLines(displayText(leftValue, json, mode, text), displayText(rightValue, json, mode, text));
  return diff.left.filter(line => line.changed).length + diff.right.filter(line => line.changed).length;
}

export function sideLines(leftValue: string | null, rightValue: string | null, json: boolean, side: 'left' | 'right', mode: RenderMode, text: TextFn): DiffLine[] {
  const left = displayText(leftValue, json, mode, text);
  const right = displayText(rightValue, json, mode, text);
  if (leftValue === null || rightValue === null) {
    return (side === 'left' ? left : right).split('\n').map(line => ({ text: line, changed: false }));
  }
  return diffLines(left, right)[side];
}

export function comparedLines(row: ConfigurationWaterfall, left: Environment, right: Environment, kind: PayloadKind, side: 'left' | 'right', mode: RenderMode, text: TextFn): DiffLine[] {
  return sideLines(payload(row, left, kind), payload(row, right, kind), kind === 'ApiCore', side, mode, text);
}

function samePayloads(left: ConfigurationWaterfall, right: ConfigurationWaterfall, environment: Environment): boolean {
  return payload(left, environment, 'Application') === payload(right, environment, 'Application')
    && payload(left, environment, 'ApiCore') === payload(right, environment, 'ApiCore');
}

function sameContent(row: ConfigurationWaterfall, left: Environment, right: Environment): boolean {
  return payload(row, left, 'Application') === payload(row, right, 'Application')
    && payload(row, left, 'ApiCore') === payload(row, right, 'ApiCore');
}

export function pairCount(row: ConfigurationWaterfall, pair: number, mode: RenderMode, lang: string, text: TextFn): number {
  const key = `${mode}:${lang}:${pair}`;
  let cached = pairCountCache.get(row);
  if (!cached) {
    cached = new Map();
    pairCountCache.set(row, cached);
  }
  const found = cached.get(key);
  if (found !== undefined) return found;
  const [left, right] = environmentPairs[pair];
  const count = changedPayloadLines(payload(row, left, 'Application'), payload(row, right, 'Application'), false, mode, text)
    + changedPayloadLines(payload(row, left, 'ApiCore'), payload(row, right, 'ApiCore'), true, mode, text);
  cached.set(key, count);
  return count;
}

export function canCompare(row: ConfigurationWaterfall, pair: number): boolean {
  const [left, right] = environmentPairs[pair];
  return hasData(row, left) && hasData(row, right);
}

export function pairBadge(row: ConfigurationWaterfall, pair: number, mode: RenderMode, lang: string, text: TextFn): string {
  const count = pairCount(row, pair, mode, lang, text);
  if (count > 0) return `!= ${count}`;
  const [left, right] = environmentPairs[pair];
  return sameContent(row, left, right) ? '=' : '!=';
}

function adjacentPair(left: Environment, right: Environment): number | null {
  const order: Environment[] = ['stable', 'alfa', 'beta', 'prod'];
  const from = order.indexOf(left);
  const to = order.indexOf(right);
  return Math.abs(from - to) === 1 ? Math.min(from, to) : null;
}

export function pairStatus(row: ConfigurationWaterfall, left: Environment, right: Environment, mode: RenderMode, lang: string, text: TextFn): 'missing' | 'equal' | 'different' {
  if (!hasData(row, left) || !hasData(row, right)) return 'missing';
  const pair = adjacentPair(left, right);
  if (pair !== null) return pairBadge(row, pair, mode, lang, text) === '=' ? 'equal' : 'different';
  return sameContent(row, left, right) ? 'equal' : 'different';
}

export function pairSymbol(row: ConfigurationWaterfall, left: Environment, right: Environment, mode: RenderMode, lang: string, text: TextFn): string {
  if (pairStatus(row, left, right, mode, lang, text) === 'equal') return '=';
  const pair = adjacentPair(left, right);
  return pair === null ? '!=' : pairBadge(row, pair, mode, lang, text);
}

export function crossStatus(left: ConfigurationWaterfall | null, right: ConfigurationWaterfall | null, environment: Environment): 'missing' | 'equal' | 'different' {
  if (!left || !right || !hasData(left, environment) || !hasData(right, environment)) return 'missing';
  return samePayloads(left, right, environment) ? 'equal' : 'different';
}

function crossLineCount(left: ConfigurationWaterfall, right: ConfigurationWaterfall, environment: Environment, mode: RenderMode, lang: string, text: TextFn): number {
  const key = `${environment}:${mode}:${lang}:${identity(right)}`;
  let cached = crossCountCache.get(left);
  if (!cached) {
    cached = new Map();
    crossCountCache.set(left, cached);
  }
  const found = cached.get(key);
  if (found !== undefined) return found;
  const count = changedPayloadLines(payload(left, environment, 'Application'), payload(right, environment, 'Application'), false, mode, text)
    + changedPayloadLines(payload(left, environment, 'ApiCore'), payload(right, environment, 'ApiCore'), true, mode, text);
  cached.set(key, count);
  return count;
}

export function crossSymbol(left: ConfigurationWaterfall | null, right: ConfigurationWaterfall | null, environment: Environment, mode: RenderMode, lang: string, text: TextFn): string {
  const status = crossStatus(left, right, environment);
  if (status !== 'different' || !left || !right) return status === 'equal' ? '=' : '!=';
  const count = crossLineCount(left, right, environment, mode, lang, text);
  return count > 0 ? `!= ${count}` : '!=';
}

export function editorMeta(row: ConfigurationWaterfall, environment: Environment, locale: string): { name: string | null; ago: string | null; color: string } | null {
  const name = updatedBy(row, environment);
  const ago = updatedAgo(row, environment, locale);
  if (!name && !ago) return null;
  return { name, ago, color: editorColors[environment] };
}

export function editKind(row: ConfigurationWaterfall): PayloadKind {
  return row.type === 'api-core-configuration' ? 'ApiCore' : 'Application';
}

export function editorText(value: string | null, kind: PayloadKind): string {
  if (!value) return '';
  return kind === 'ApiCore' ? toEditorJson(value) : value;
}

export function promptEditorText(value: string | null): string {
  if (!value) return '';
  try { return JSON.stringify(JSON.parse(value), null, 2); }
  catch { return value; }
}

export function presentDraft(value: string, json: boolean, mode: RenderMode): string {
  let text = value;
  if (json && value.trim()) {
    try { text = JSON.stringify(JSON.parse(value), null, 2); }
    catch { text = value; }
  }
  return mode === 'rendered' ? text.replaceAll('\\n', '\n') : text;
}

export function lineNumbers(value: string): string {
  const count = Math.max(1, value.split('\n').length);
  let text = '1';
  for (let number = 2; number <= count; number++) text += '\n' + number;
  return text;
}

export function lineDigits(value: string): number {
  return String(Math.max(1, value.split('\n').length)).length;
}

export function initials(name: string): string {
  const parts = name.split(/[\s@._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0] ?? name).slice(0, 2).toUpperCase();
}

export function toEditorJson(raw: string): string {
  try { return expandJsonNewlines(JSON.stringify(JSON.parse(raw), null, 2)); }
  catch { return raw; }
}

export function fromEditorJson(editor: string): string {
  return JSON.stringify(JSON.parse(foldJsonNewlines(editor)));
}

function updatedBy(row: ConfigurationWaterfall, environment: Environment): string | null {
  switch (environment) {
    case 'stable': return row.stableUpdatedBy;
    case 'alfa': return row.alfaUpdatedBy;
    case 'beta': return row.betaUpdatedBy;
    case 'prod': return row.prodUpdatedBy;
  }
}

function updatedAt(row: ConfigurationWaterfall, environment: Environment): string | null {
  switch (environment) {
    case 'stable': return row.stableUpdatedAt;
    case 'alfa': return row.alfaUpdatedAt;
    case 'beta': return row.betaUpdatedAt;
    case 'prod': return row.prodUpdatedAt;
  }
}

function updatedAgo(row: ConfigurationWaterfall, environment: Environment, locale: string): string | null {
  const date = parseUpdatedAt(updatedAt(row, environment));
  return date ? relativeTime(date, locale) : null;
}

function expandJsonNewlines(pretty: string): string {
  let out = '';
  let inString = false;
  let escaped = false;
  for (const ch of pretty) {
    if (!inString) {
      out += ch;
      if (ch === '"') inString = true;
      continue;
    }
    if (escaped) {
      out += ch === 'n' ? '\n' : ch === 't' ? '\t' : '\\' + ch;
      escaped = false;
      continue;
    }
    if (ch === '\\') {
      escaped = true;
      continue;
    }
    out += ch;
    if (ch === '"') inString = false;
  }
  if (escaped) out += '\\';
  return out;
}

function foldJsonNewlines(editor: string): string {
  let out = '';
  let inString = false;
  let escaped = false;
  for (const ch of editor) {
    if (!inString) {
      out += ch;
      if (ch === '"') inString = true;
      continue;
    }
    if (escaped) {
      out += '\\' + ch;
      escaped = false;
      continue;
    }
    if (ch === '\\') {
      escaped = true;
      continue;
    }
    if (ch === '"') {
      inString = false;
      out += ch;
      continue;
    }
    if (ch === '\r') continue;
    if (ch === '\n') { out += '\\n'; continue; }
    if (ch === '\t') { out += '\\t'; continue; }
    out += ch;
  }
  if (escaped) out += '\\';
  return out;
}

export function parseUpdatedAt(value: string | null): Date | null {
  if (!value) return null;
  const match = value.trim().match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2}(?:\.\d+)?)(Z|[+-]\d{2}(?::?\d{2})?)?$/);
  const normalized = match
    ? `${match[1]}T${match[2]}${normalizeOffset(match[3])}`
    : value.trim();
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

function normalizeOffset(offset: string | undefined): string {
  if (!offset || offset === 'Z') return 'Z';
  const compact = offset.replace(':', '');
  return `${compact.slice(0, 3)}:${compact.slice(3).padEnd(2, '0')}`;
}

export function relativeTime(date: Date, locale: string): string {
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'always' });
  const seconds = Math.round((date.getTime() - Date.now()) / 1000);
  const abs = Math.abs(seconds);
  if (abs < 60) return rtf.format(seconds, 'second');
  if (abs < 3600) return rtf.format(Math.round(seconds / 60), 'minute');
  if (abs < 86_400) return rtf.format(Math.round(seconds / 3600), 'hour');
  if (abs < 604_800) return rtf.format(Math.round(seconds / 86_400), 'day');
  if (abs < 4_838_400) return rtf.format(Math.round(seconds / 604_800), 'week');
  if (abs < 31_557_600) return rtf.format(Math.round(seconds / 2_629_800), 'month');
  return rtf.format(Math.round(seconds / 31_557_600), 'year');
}
